import React, { useEffect } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import {
	EndorsementContext,
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import AuxDataView from '../../auxData/AuxDataView';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import ProtectedRoute from '../../ProtectedRoute';
import EndorsementExposure from '../Endorsements/Exposure';
import EndorsementPeril from '../Endorsements/Peril';
import EndorsementPerils from '../Endorsements/Perils';
interface Props {
	endorsementData: EndorsementData;
}

/**
 * Route: '...endorsements/:id/exposures/:exposureId'
 * The exposureId can have the format 'added-0, ...'.
 * This is the only endorsement component that should set the selectedExposure
 */
const EndorsementExposureRoutes = (props: Props) => {
	const { endorsementData } = props;
	const {
		policy,
		endorsementUpdateObj,
		selectedExposure,
		setSelectedExposure,
	} = endorsementData;
	const navigate = useNavigate();
	const params = useParams();
	const { exposureId } = params;
	const currentExposures = policy.exposures;
	const addedExposures = endorsementUpdateObj.addExposures;
	const isAdded = exposureId.split('-')[0] === 'added';

	useEffect(() => {
		if (isAdded) {
			const addedIndex = parseInt(exposureId.split('-')[1], 10);
			// type ExposureCreateRequest
			setSelectedExposure(addedExposures[addedIndex]);
		} else {
			// type Exposure
			setSelectedExposure(
				currentExposures.find(
					(exposure: any) => exposure.displayId === exposureId,
				),
			);
		}

		return () => {
			setSelectedExposure(null);
		};
	}, [exposureId, policy.exposures, endorsementUpdateObj.addExposures]);

	if (!selectedExposure) {
		// add redirect back to
		return <LargeCenterSpinner />;
	}

	const exposureLocator =
		(selectedExposure as Exposure).locator ||
		(selectedExposure as ExposureCreateRequest).exposureLocator;

	return (
		<EndorsementContext.Provider value={endorsementData}>
			<Routes>
				<Route
					path='auxData'
					element={
						<ProtectedRoute permission='auxData.fetch'>
							<AuxDataView
								navigate={navigate}
								entityLocator={exposureLocator}
								entityType={'exposure'}
							/>
						</ProtectedRoute>
					}
				/>
				<Route path='perils'>
					<Route index element={<EndorsementPerils />} />
					<Route path=':perilId'>
						<Route index element={<EndorsementPeril />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityPath={'perilId'}
										entityType={'peril'}
									/>
								</ProtectedRoute>
							}
						/>
					</Route>
				</Route>
				<Route index element={<EndorsementExposure />} />
				<Route path='*' element={<Navigate to='/' />} />
			</Routes>
		</EndorsementContext.Provider>
	);
};

export default withEndorsementContext<Props>(EndorsementExposureRoutes);
